import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { getConversation, sendTurn, respondConversation, getInspector } from "@/lib/api";
import { TalkInput } from "@/components/TalkInput";
import { SparkSun, GoldThreadLine } from "@/components/SparkSun";
import { ThreadEcho } from "@/components/ThreadEcho";
import { toast } from "sonner";

const Choice = ({ turn, onRespond, busy, synthesis }) => {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const opts = synthesis
    ? [["yes", "Yes — that's it"], ["almost", "Almost — I'd change something"], ["no", "No — we've missed something"]]
    : [["yes", "Yes"], ["partly", "Partly"], ["no", "No"]];
  if (turn.status) return <p data-testid="c1-answered" className="mt-4 text-xs text-[#8A8A91]">You said: {turn.status}{turn.correction ? ` — "${turn.correction}"` : ""}</p>;
  return (
    <div className="mt-6">
      {!mode ? (
        <div className="flex flex-wrap gap-2">
          {opts.map(([k, label]) => (
            <button key={k} data-testid={`c1-${synthesis ? "synth" : "interp"}-${k}-btn`} disabled={busy}
                    onClick={() => (k === "yes" ? onRespond(turn.id, k, "") : setMode(k))}
                    className={k === "yes" ? "spark-btn-copper disabled:opacity-60" : "spark-btn-secondary"}>{label}</button>
          ))}
        </div>
      ) : (
        <div className="max-w-2xl">
          <p className="spark-voice text-lg text-[#17171C] mb-3">
            {synthesis ? (mode === "almost" ? "What would you change?" : "What have I misunderstood, or missed?") : (mode === "partly" ? "Which part fits, and which part have I got wrong?" : "Okay. Tell me what I've misunderstood.")}
          </p>
          <TalkInput testid="c1-correction-input" rows={3} value={text} onChange={setText} placeholder="In your own words." />
          <div className="mt-3 flex gap-2">
            <button data-testid="c1-correction-submit-btn" disabled={busy || !text.trim()} onClick={() => onRespond(turn.id, mode, text)} className="spark-btn-primary disabled:opacity-60">Continue</button>
            <button onClick={() => setMode(null)} className="spark-btn-secondary">Back</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Conversation() {
  const { userId, profile, isDev, isPilot } = useSpark();
  const nav = useNavigate();
  const [conv, setConv] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [insp, setInsp] = useState(null);
  const endRef = useRef(null);
  const canInspect = isDev || (!!sessionStorage.getItem("spark_admin"));

  useEffect(() => { getConversation(userId).then(setConv).catch(() => toast("Couldn't open the conversation. Please try again.")); }, [userId]);

  useEffect(() => {
    if (!conv?.thinking) return undefined;
    const t = setInterval(async () => {
      try {
        const c = await getConversation(userId);
        setConv(c);
        if (!c.thinking) { clearInterval(t); if (c.error) toast(c.error); }
      } catch { /* transient */ }
    }, 2500);
    return () => clearInterval(t);
  }, [conv?.thinking, userId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [conv?.turns?.length, conv?.thinking]);

  const send = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try { setConv(await sendTurn(userId, draft.trim())); setDraft(""); }
    catch (e) { toast(e?.response?.data?.detail || "Something interrupted us. Your words are still here — try again."); }
    finally { setBusy(false); }
  };
  const respond = async (turnId, status, correction) => {
    setBusy(true);
    try { setConv(await respondConversation(userId, { turn_id: turnId, status, correction })); }
    catch (e) { toast(e?.response?.data?.detail || "Something interrupted us. Please try again."); }
    finally { setBusy(false); }
  };
  const openInspector = async () => {
    try { setInsp(await getInspector(userId, sessionStorage.getItem("spark_admin") || undefined)); } catch { toast("Inspector not available."); }
  };

  if (!conv) return <div className="text-[#8A8A91]">Opening your conversation…</div>;
  const turns = conv.turns || [];
  const lastId = turns[turns.length - 1]?.id;
  const complete = conv.status === "complete";
  const awaiting = turns[turns.length - 1]?.role === "spark" && ["interpretation", "synthesis"].includes(turns[turns.length - 1]?.kind) && !turns[turns.length - 1]?.status;

  return (
    <div className="max-w-3xl mx-auto" data-testid="conversation-one">
      <div className="flex items-center gap-3 mb-10">
        <SparkSun size={28} color="#C3A56B" />
        <p className="spark-eyebrow">Why am I here?</p>
        {canInspect && <button data-testid="c1-inspector-btn" onClick={openInspector} className="ml-auto text-xs text-[#8A8A91] hover:text-[#1F3D80]">Inspector</button>}
      </div>

      <ol className="space-y-10" data-testid="c1-journal">
        {turns.map((t, i) => {
          const current = t.id === lastId || (awaiting && i >= turns.length - 2);
          const cls = current ? "opacity-100" : "opacity-60";
          if (t.role === "client") {
            return (
              <li key={t.id} data-testid="c1-client-turn" className={`pl-6 sm:pl-10 border-l border-[rgba(0,0,0,0.10)] ${cls} transition-opacity`}>
                <p className="text-lg leading-relaxed text-[#17171C] whitespace-pre-wrap">{t.text}</p>
              </li>
            );
          }
          if (t.kind === "synthesis") {
            const s = t.synthesis || {};
            return (
              <li key={t.id} data-testid="c1-synthesis" className={`${cls} transition-opacity`}>
                <GoldThreadLine className="w-32 h-2 mb-6" />
                <p className="spark-eyebrow-copper">Your Starting Point</p>
                <p data-testid="c1-starting-point" className="mt-2 spark-voice text-2xl leading-snug text-[#17171C]">{s.starting_point}</p>
                {s.heart && (<><p className="spark-eyebrow-copper mt-8">What seems to be at the heart of it</p><p data-testid="c1-heart" className="mt-2 spark-voice text-xl leading-snug text-[#17171C]">{s.heart}</p></>)}
                <p className="spark-eyebrow-copper mt-8">The question you're really working through</p>
                <p data-testid="c1-career-question" className="mt-2 spark-voice text-2xl leading-snug" style={{ color: "#1F3D80" }}>{s.career_question}</p>
                {!complete && <Choice turn={t} onRespond={respond} busy={busy || conv.thinking} synthesis />}
                {complete && <p data-testid="c1-complete" className="mt-6 text-sm text-[#8A8A91]">Held. This is where you're starting from.</p>}
              </li>
            );
          }
          return (
            <li key={t.id} data-testid={`c1-spark-turn`} data-kind={t.kind} className={`${cls} transition-opacity`}>
              <p className={`spark-voice leading-snug text-[#17171C] whitespace-pre-wrap ${current ? "text-2xl sm:text-3xl" : "text-xl"}`}>{t.text}</p>
              <ThreadEcho theme={t.thread_echo} className="mt-3" />
              {t.kind === "interpretation" && <Choice turn={t} onRespond={respond} busy={busy || conv.thinking} />}
            </li>
          );
        })}
        {conv.thinking && <li data-testid="c1-thinking" className="text-sm text-[#8A8A91]">Spark is listening…</li>}
      </ol>

      {!complete && !awaiting && !conv.thinking && (
        <div className="mt-12" data-testid="c1-composer">
          <div className="spark-thread mb-6" />
          <TalkInput testid="c1-input" rows={5} warm value={draft} onChange={setDraft} placeholder="Write your response…" />
          <div className="mt-3 flex justify-end">
            <button data-testid="c1-send-btn" disabled={busy || !draft.trim()} onClick={send} className="spark-btn-primary disabled:opacity-60">Continue</button>
          </div>
        </div>
      )}
      {complete && (
        <div className="mt-12 flex gap-3">
          <button data-testid="c1-go-dashboard-btn" onClick={() => nav("/dashboard")} className="spark-btn-primary">Go to my journey</button>
        </div>
      )}
      <div ref={endRef} />

      {insp && (
        <aside data-testid="c1-inspector" className="fixed inset-y-0 right-0 w-full sm:w-[520px] overflow-y-auto bg-white border-l border-[rgba(0,0,0,0.1)] p-6 text-xs z-50">
          <div className="flex items-center justify-between mb-4"><p className="spark-eyebrow">Inspector (dev)</p><button onClick={() => setInsp(null)} className="text-[#8A8A91]">Close</button></div>
          <p className="spark-eyebrow mb-2">Memory ({insp.memory.length})</p>
          <ul className="space-y-1 mb-6">{insp.memory.map(m => <li key={m.id}><span className="text-[#1F3D80]">{m.provenance}</span> · {m.type} · {m.text} <span className="text-[#8A8A91]">“{m.verbatim}”</span></li>)}</ul>
          <p className="spark-eyebrow mb-2">Rejected ({insp.rejected.length})</p>
          <ul className="space-y-1 mb-6">{insp.rejected.map((x, i) => <li key={i}>{x.status}: {x.text} — “{x.correction}”</li>)}</ul>
          <p className="spark-eyebrow mb-2">Decisions ({insp.inspector.length})</p>
          <ul className="space-y-3">{insp.inspector.map((d, i) => <li key={i} className="border-t border-[rgba(0,0,0,0.08)] pt-2"><pre className="whitespace-pre-wrap">{JSON.stringify(d, null, 1)}</pre></li>)}</ul>
        </aside>
      )}
    </div>
  );
}
